import { useState } from "react";
import { RadioGroup, RadioGroupItem } from "../../../components/ui/radio-group";
import { Label } from "../../../components/ui/label";
import { Button } from "../../../components/ui/button";

/**
 * Master v5 Prompt 11 (§4.5, backed by audience_gate_contracts.py) —
 * the blocking "Who should see this?" question. No option is ever
 * preselected here either — `choice` starts as null and the confirm
 * button stays disabled until the user makes an explicit pick, matching
 * the backend's "no default value" rule exactly. This component holds
 * no state between mounts — every open is a fresh, unremembered choice.
 */
const CLIENT_OPTIONS = [
  { value: "only_me_and_coach", label: "Only me & my coach" },
  { value: "my_team_and_coach", label: "My team & coach" },
];
const COACH_OPTIONS = [
  { value: "individual_member", label: "Individual member" },
  { value: "whole_team", label: "Whole team" },
];
export const AUDIENCE_OPTION_LABELS = Object.fromEntries([...CLIENT_OPTIONS, ...COACH_OPTIONS].map((o) => [o.value, o.label]));

export default function AudienceGate({ actorRole, teamMembers = [], onConfirm, disabled, actionType = "team_upload" }) {
  const [choice, setChoice] = useState(null);
  const [targetMemberId, setTargetMemberId] = useState(null);
  const options = actorRole === "coach" ? COACH_OPTIONS : CLIENT_OPTIONS;
  const needsMemberPicker = actorRole === "coach" && choice === "individual_member";
  const canConfirm = choice && (!needsMemberPicker || targetMemberId);

  function confirm() {
    if (!canConfirm) return;
    onConfirm({
      action_type: actionType, actor_role: actorRole, choice,
      target_membership_ids: choice === "individual_member" ? [targetMemberId] : [],
    });
  }

  return (
    <div className="rounded-lg border border-ink/15 bg-paper/60 p-4 space-y-3" data-testid="audience-gate">
      <p className="text-sm font-medium text-ink">Who should see this?</p>
      <RadioGroup value={choice || ""} onValueChange={(v) => { setChoice(v); setTargetMemberId(null); }} data-testid="audience-gate-options">
        {options.map((opt) => (
          <div key={opt.value} className="flex items-center gap-2">
            <RadioGroupItem value={opt.value} id={`audience-${opt.value}`} data-testid={`audience-gate-option-${opt.value}`} />
            <Label htmlFor={`audience-${opt.value}`} className="text-sm text-ink-soft cursor-pointer">{opt.label}</Label>
          </div>
        ))}
      </RadioGroup>
      {needsMemberPicker && (
        <div className="space-y-1.5" data-testid="audience-gate-member-picker">
          <p className="text-xs text-ink-soft">Which member?</p>
          <RadioGroup value={targetMemberId || ""} onValueChange={setTargetMemberId}>
            {teamMembers.map((m) => (
              <div key={m.membership_id} className="flex items-center gap-2">
                <RadioGroupItem value={m.membership_id} id={`member-${m.membership_id}`} data-testid={`audience-gate-member-${m.membership_id}`} />
                <Label htmlFor={`member-${m.membership_id}`} className="text-sm text-ink-soft cursor-pointer">{m.display_name}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>
      )}
      <Button size="sm" disabled={!canConfirm || disabled} onClick={confirm} data-testid="audience-gate-confirm-btn">
        {choice === "individual_member" && targetMemberId
          ? `Send to ${teamMembers.find((m) => m.membership_id === targetMemberId)?.display_name || "member"}`
          : choice ? `Send to ${options.find((o) => o.value === choice)?.label}` : "Confirm audience"}
      </Button>
    </div>
  );
}
